/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { strings } from "../../../common/Strings/Strings";
import { getGeneralError } from "../../../util/helperFunctions";
import { UserDeletionRequest } from "../../../models/user/UserDeletionRequest";
import CloseButton from "../../../components/CloseButton";
import AlertBox, { AlertType } from "../../../components/AlertBox";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal/Modal";
import UserManagementApi from "../../../api/UserManagementApi";
import ManualOwnerPasswordInput from "../../Clinic/Settings/BasicInfos/DeleteClinic/ManualOwnerPasswordInput";
import UserProfilePicture from "../../../components/Pictures/User/UserProfilePicture";
import { UserDetailsResponse } from "../../../models/management/UserDetailsResponse";

interface Props {
  user: UserDetailsResponse;
  isOpen: boolean;
  close(): void;
}

const defaultFormValues = {
  password: "",
};

const DeleteUserModal: React.FC<Props> = (props: Props) => {
  const { user, isOpen, close } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<UserDeletionRequest>({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const closeModal = (): void => {
    close();
    setError(null);
    reset(defaultFormValues);
  };

  const submitDeleteForm = async (data: UserDeletionRequest) => {
    setLoading(true);

    try {
      await UserManagementApi.deleteUser(user.userId, data);
      setLoading(false);
      close();
    } catch (err) {
      setLoading(false);
      setError(await getGeneralError(err));
    }
  };

  return (
    <Modal handleClose={closeModal} show={isOpen}>
      <Modal.Header title={strings.deleteProfile} />
      <Modal.Body>
        <div className="flex flex-row items-center">
          <div className="w-20">
            <UserProfilePicture userId={user.userId} pictureId={user.profilePicture} />
          </div>
          <p className="ml-3 text-lg font-bold">{user.fullName || user.emails[0].value}</p>
        </div>
        <AlertBox closeAble={false} type={AlertType.WARNING} message={strings.deleteProfileWarning} />
        <div className="mt-10 mb-5">{strings.deleteProfileCredentials}</div>
        <form onSubmit={handleSubmit(submitDeleteForm)} id="deleteUserForm">
          <ManualOwnerPasswordInput
            name="password"
            label={strings.password}
            register={register}
            error={errors.password}
          />
        </form>
        <AlertBox message={error} />
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="modal-main-button"
          disabled={!isValid}
          form="deleteUserForm"
          loading={loading}
          type="submit"
          variant="danger"
        >
          {strings.delete}
        </Button>
        <CloseButton onClick={closeModal} text={strings.cancel} />
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteUserModal;
